// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Financeiros/IntegracaoBancaria.tsx
//
//  Tela ÚNICA de integração bancária por API (saldo, extrato, cobrança, PIX,
//  webhooks). É BANK-AGNOSTIC: conversa só com BancoIntegracaoProvider, resolvido
//  pela conta corrente selecionada (Inter hoje; outros bancos amanhã). As abas
//  aparecem conforme as capabilities do provider.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet, RefreshCw, FileText, QrCode, Webhook, Loader2, Copy, Download, Landmark,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/apiClient';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { useDebounce } from '@/hooks/useDebounce';
import { TituloReceberSearchCombobox } from '@/components/TituloReceberSearchCombobox';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';
import { useBancoIntegracaoProvider } from '@/features/bancoIntegracao/hooks/useBancoIntegracaoProvider';
import { WEBHOOK_TIPOS, type WebhookTipo, type BancoSaldo, type CobrancaSituacao, type PixCobResultado } from '@/features/bancoIntegracao/types';

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtDataBr = (s: string) => new Date(s).toLocaleDateString('pt-BR');
const hoje = () => new Date().toISOString().slice(0, 10);
const diasAtras = (n: number) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

export default function IntegracaoBancaria() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [contas, setContas] = useState<{ id: number; descricao: string }[]>([]);
  const [contaId, setContaId] = useState<number | null>(null);
  const { provider, carregando: carregandoProvider } = useBancoIntegracaoProvider(contaId);

  useEffect(() => {
    cadastrosFinanceirosService.getContasCorrentesCombo()
      .then((lista) => {
        const arr = lista as unknown as { id: number; descricao: string }[];
        setContas(arr);
        if (arr.length > 0) setContaId((cur) => cur ?? arr[0].id);
      })
      .catch(() => toast({ title: 'Erro ao carregar contas correntes', variant: 'destructive' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const caps = provider?.capabilities;

  return (
    <div className="space-y-6 p-1">
      <div className="flex items-center gap-3">
        <Landmark className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Integração Bancária</h1>
          <p className="text-sm text-muted-foreground">
            Saldo, extrato, cobranças, PIX e webhooks direto pela API do banco.
          </p>
        </div>
      </div>

      {/* Seletor de conta corrente */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <div className="min-w-[280px] flex-1">
            <Label>Conta corrente</Label>
            <Select
              value={contaId?.toString() ?? ''}
              onValueChange={(v) => setContaId(Number(v))}
            >
              <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
              <SelectContent>
                {contas.map((c) => (
                  <SelectItem key={c.id} value={c.id.toString()}>{c.descricao}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {provider && (
            <Badge variant="secondary" className="gap-1">
              <Landmark className="h-3 w-3" /> {provider.nome}
            </Badge>
          )}
        </CardContent>
      </Card>

      {carregandoProvider && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando integração…
        </div>
      )}

      {!carregandoProvider && contaId && !provider && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Esta conta corrente não tem integração bancária por API configurada (ou o banco
            ainda não é suportado). Configure a integração no cadastro da conta corrente.
          </CardContent>
        </Card>
      )}

      {provider && contaId && caps && (
        <Tabs defaultValue={caps.saldo ? 'extrato' : caps.cobranca ? 'cobranca' : 'pix'}>
          <TabsList>
            {(caps.saldo || caps.extrato) && <TabsTrigger value="extrato"><Wallet className="mr-1 h-4 w-4" />Saldo & Extrato</TabsTrigger>}
            {caps.cobranca && <TabsTrigger value="cobranca"><FileText className="mr-1 h-4 w-4" />Cobranças</TabsTrigger>}
            {caps.pix && <TabsTrigger value="pix"><QrCode className="mr-1 h-4 w-4" />PIX</TabsTrigger>}
            {caps.webhooks && <TabsTrigger value="webhooks"><Webhook className="mr-1 h-4 w-4" />Webhooks</TabsTrigger>}
          </TabsList>

          {(caps.saldo || caps.extrato) && (
            <TabsContent value="extrato">
              <SaldoExtratoTab contaId={contaId} provider={provider} onIrConciliacao={() => navigate('/financeiro/conciliacao-ofx')} />
            </TabsContent>
          )}
          {caps.cobranca && (
            <TabsContent value="cobranca">
              <CobrancaTab contaId={contaId} provider={provider} />
            </TabsContent>
          )}
          {caps.pix && (
            <TabsContent value="pix">
              <PixTab contaId={contaId} provider={provider} />
            </TabsContent>
          )}
          {caps.webhooks && (
            <TabsContent value="webhooks">
              <WebhooksTab contaId={contaId} provider={provider} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}

// ── Tab: Saldo & Extrato ──────────────────────────────────────────────────────

function SaldoExtratoTab({ contaId, provider, onIrConciliacao }: {
  contaId: number;
  provider: ReturnType<typeof useBancoIntegracaoProvider>['provider'];
  onIrConciliacao: () => void;
}) {
  const { toast } = useToast();
  const [saldo, setSaldo] = useState<BancoSaldo | null>(null);
  const [carregandoSaldo, setCarregandoSaldo] = useState(false);
  const [dataInicio, setDataInicio] = useState(diasAtras(30));
  const [dataFim, setDataFim] = useState(hoje());
  const [sincronizando, setSincronizando] = useState(false);

  const consultarSaldo = useCallback(async () => {
    if (!provider) return;
    setCarregandoSaldo(true);
    try {
      setSaldo(await provider.consultarSaldo(contaId));
    } catch (e) {
      toast({ title: 'Erro ao consultar saldo', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally { setCarregandoSaldo(false); }
  }, [provider, contaId, toast]);

  const sincronizar = async () => {
    if (!provider) return;
    setSincronizando(true);
    try {
      const r = await provider.sincronizarExtrato(contaId, dataInicio, dataFim);
      toast({
        title: 'Extrato sincronizado',
        description: `${r.qtdImportados} novo(s), ${r.qtdDuplicados} já existente(s), de ${r.qtdLidos} lido(s).`,
      });
    } catch (e) {
      toast({ title: 'Erro ao sincronizar extrato', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally { setSincronizando(false); }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader><CardTitle className="text-base">Saldo</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={consultarSaldo} disabled={carregandoSaldo} variant="outline">
            {carregandoSaldo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wallet className="mr-2 h-4 w-4" />}
            Consultar saldo
          </Button>
          {saldo && (
            <div className="grid grid-cols-3 gap-3 text-center">
              <SaldoCard titulo="Disponível" valor={saldo.disponivel} destaque />
              <SaldoCard titulo="Bloqueado" valor={saldo.bloqueado} />
              <SaldoCard titulo="Limite" valor={saldo.limite} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Sincronizar extrato</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Label>De</Label>
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div className="flex-1">
              <Label>Até</Label>
              <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={sincronizar} disabled={sincronizando}>
              {sincronizando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Sincronizar
            </Button>
            <Button variant="ghost" onClick={onIrConciliacao}>Ir para conciliação →</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            O extrato é gravado na mesma base do OFX — depois concilie normalmente.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function SaldoCard({ titulo, valor, destaque }: { titulo: string; valor: number; destaque?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${destaque ? 'bg-primary/5' : ''}`}>
      <div className="text-xs text-muted-foreground">{titulo}</div>
      <div className={`text-lg font-semibold ${destaque ? 'text-primary' : ''}`}>{fmtCur(valor)}</div>
    </div>
  );
}

// ── Tab: Cobranças ────────────────────────────────────────────────────────────

function CobrancaTab({ contaId, provider }: {
  contaId: number;
  provider: NonNullable<ReturnType<typeof useBancoIntegracaoProvider>['provider']>;
}) {
  const { toast } = useToast();
  const [contaReceberId, setContaReceberId] = useState<number | null>(null);
  const [multa, setMulta] = useState('');
  const [mora, setMora] = useState('');
  const [desconto, setDesconto] = useState('');
  const [mensagem, setMensagem] = useState('');
  // PIX no boleto: '__padrao__' = o que o contrato do banco define.
  const [pix, setPix] = useState<'__padrao__' | 'sim' | 'nao'>('__padrao__');
  const [emitindo, setEmitindo] = useState(false);

  const [codigo, setCodigo] = useState('');
  const [situacao, setSituacao] = useState<CobrancaSituacao | null>(null);
  const [consultando, setConsultando] = useState(false);

  // Grade de cobranças emitidas (registro local, paginado no servidor).
  const [filtroIni, setFiltroIni] = useState(diasAtras(30));
  const [filtroFim, setFiltroFim] = useState(hoje());
  const [filtroBusca, setFiltroBusca] = useState('');
  const buscaDebounced = useDebounce(filtroBusca, 300);
  const paginacao = usePaginacaoServidor({ resetarQuando: [contaId, filtroIni, filtroFim, buscaDebounced] });
  const { data: lista, isLoading: carregandoLista, refetch: refetchLista } = useQuery({
    queryKey: ['cobrancas-emitidas', provider.id, contaId, filtroIni, filtroFim, buscaDebounced, paginacao.pagina, paginacao.tamanhoPagina],
    queryFn: () => provider.listarCobrancas(contaId, {
      dtInicio: filtroIni, dtFim: filtroFim, busca: buscaDebounced,
      pagina: paginacao.pagina, tamanhoPagina: paginacao.tamanhoPagina,
    }),
    placeholderData: keepPreviousData,
  });

  const emitir = async () => {
    const crId = Number(contaReceberId);
    if (!crId) { toast({ title: 'Escolha o título a receber', variant: 'destructive' }); return; }
    setEmitindo(true);
    try {
      const r = await provider.emitirCobranca(contaId, crId, {
        multaPercentual: Number(multa) || 0,
        moraPercentual: Number(mora) || 0,
        descontoPercentual: Number(desconto) || 0,
        mensagem: mensagem || undefined,
        comPix: pix === '__padrao__' ? null : pix === 'sim',
      });
      setCodigo(r.codigoSolicitacao);
      setSituacao(null);
      refetchLista();
      toast({ title: 'Cobrança emitida', description: `Código ${r.codigoSolicitacao}` });
    } catch (e) {
      toast({ title: 'Erro ao emitir cobrança', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally { setEmitindo(false); }
  };

  const consultar = async () => {
    if (!codigo) return;
    setConsultando(true);
    try { setSituacao(await provider.consultarCobranca(contaId, codigo)); refetchLista(); }
    catch (e) { toast({ title: 'Erro ao consultar', description: getApiErrorMessage(e), variant: 'destructive' }); }
    finally { setConsultando(false); }
  };

  const baixarPdf = async () => {
    try {
      const blob = await provider.baixarCobrancaPdf(contaId, codigo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `boleto-${codigo}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Erro ao baixar PDF', description: getApiErrorMessage(e), variant: 'destructive' }); }
  };

  const cancelar = async () => {
    const motivo = window.prompt('Motivo do cancelamento:');
    if (!motivo) return;
    try {
      await provider.cancelarCobranca(contaId, codigo, motivo);
      toast({ title: 'Cobrança cancelada' });
      consultar();
    } catch (e) { toast({ title: 'Erro ao cancelar', description: getApiErrorMessage(e), variant: 'destructive' }); }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader><CardTitle className="text-base">Emitir cobrança (boleto + PIX)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Título a receber *</Label>
            <TituloReceberSearchCombobox value={contaReceberId} onChange={setContaReceberId} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Multa %</Label><Input type="number" value={multa} onChange={(e) => setMulta(e.target.value)} /></div>
            <div><Label>Mora % a.m.</Label><Input type="number" value={mora} onChange={(e) => setMora(e.target.value)} /></div>
            <div><Label>Desconto %</Label><Input type="number" value={desconto} onChange={(e) => setDesconto(e.target.value)} /></div>
          </div>
          <div><Label>Mensagem (opcional)</Label><Input value={mensagem} onChange={(e) => setMensagem(e.target.value)} /></div>
          {provider.opcoesPixNoBoleto && (
            <div>
              <Label>QR Code PIX no boleto</Label>
              <Select value={pix} onValueChange={(v) => setPix(v as typeof pix)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__padrao__">Padrão do contrato</SelectItem>
                  <SelectItem value="sim">Incluir</SelectItem>
                  <SelectItem value="nao">Não incluir</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {provider.cobrancaHerdaConta && (
            <p className="text-xs text-muted-foreground">
              Campos em branco usam a configuração da conta corrente (juros, multa, desconto, dias de protesto e
              descrição do boleto). As instruções saem impressas no boleto.
            </p>
          )}
          <Button onClick={emitir} disabled={emitindo}>
            {emitindo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
            Emitir cobrança
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Situação da cobrança</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!codigo && (
            <p className="text-sm text-muted-foreground">
              Escolha uma cobrança na lista abaixo para consultar a situação no banco, baixar o PDF ou cancelar.
            </p>
          )}
          {codigo && (
            <div className="text-sm">
              <span className="text-muted-foreground">{provider.rotuloCodigoCobranca ?? 'Código da solicitação'}: </span>
              <span className="font-mono break-all">{codigo}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={consultar} disabled={!codigo || consultando}>
              {consultando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Consultar
            </Button>
            <Button variant="outline" onClick={baixarPdf} disabled={!codigo}><Download className="mr-2 h-4 w-4" />PDF</Button>
            <Button variant="destructive" onClick={cancelar} disabled={!codigo}>Cancelar</Button>
          </div>
          {situacao && (
            <div className="space-y-1 rounded-lg border p-3 text-sm">
              <div className="flex justify-between"><span>Situação</span><Badge>{situacao.situacao ?? '—'}</Badge></div>
              <div className="flex justify-between"><span>Valor</span><span>{fmtCur(situacao.valorNominal)}</span></div>
              {situacao.linhaDigitavel && <div className="break-all"><b>Linha digitável:</b> {situacao.linhaDigitavel}</div>}
              {situacao.pixCopiaECola && <CopiaCola label="PIX copia-e-cola" valor={situacao.pixCopiaECola} />}
              <div className="flex justify-between"><span>Baixa aplicada</span><span>{situacao.baixaAplicada ? 'Sim' : 'Não'}</span></div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader><CardTitle className="text-base">Cobranças emitidas</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Emitidas de</Label><Input type="date" value={filtroIni} onChange={(e) => setFiltroIni(e.target.value)} className="w-40" /></div>
            <div><Label className="text-xs">até</Label><Input type="date" value={filtroFim} onChange={(e) => setFiltroFim(e.target.value)} className="w-40" /></div>
            <div className="min-w-[240px] flex-1">
              <Label className="text-xs">Buscar</Label>
              <Input value={filtroBusca} onChange={(e) => setFiltroBusca(e.target.value)} placeholder="Documento, cliente ou código" />
            </div>
          </div>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Emissão</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>{provider.rotuloCodigoCobranca ?? 'Código'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carregandoLista ? (
                  <TableRow><TableCell colSpan={7} className="py-6 text-center text-muted-foreground">Carregando…</TableCell></TableRow>
                ) : !lista || lista.itens.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="py-6 text-center text-muted-foreground">Nenhuma cobrança emitida no período.</TableCell></TableRow>
                ) : lista.itens.map((c) => (
                  <TableRow
                    key={c.codigo}
                    className={`cursor-pointer ${c.codigo === codigo ? 'bg-primary/5' : ''}`}
                    onClick={() => { setCodigo(c.codigo); setSituacao(null); }}
                  >
                    <TableCell className="whitespace-nowrap">{fmtDataBr(c.dtEmissao)}</TableCell>
                    <TableCell className="whitespace-nowrap">{c.numDocumento ?? '—'}{c.nroParcela > 0 ? ` / ${c.nroParcela}` : ''}</TableCell>
                    <TableCell className="max-w-[220px] truncate">{c.clienteNome ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{fmtDataBr(c.dtVencimento)}</TableCell>
                    <TableCell className="text-right">{fmtCur(c.valorNominal)}</TableCell>
                    <TableCell><Badge variant="outline">{c.situacao ?? '—'}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{c.codigo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {lista && lista.total > 0 && <PaginacaoRodape {...paginacao.rodape(lista)} />}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: PIX ──────────────────────────────────────────────────────────────────

function PixTab({ contaId, provider }: {
  contaId: number;
  provider: NonNullable<ReturnType<typeof useBancoIntegracaoProvider>['provider']>;
}) {
  const { toast } = useToast();
  const [valor, setValor] = useState('');
  const [comVencimento, setComVencimento] = useState(false);
  const [dataVencimento, setDataVencimento] = useState(hoje());
  const [devedorDoc, setDevedorDoc] = useState('');
  const [devedorNome, setDevedorNome] = useState('');
  const [contaReceberId, setContaReceberId] = useState<number | null>(null);
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState<PixCobResultado | null>(null);

  const [e2eid, setE2eid] = useState('');
  const [valorDevolucao, setValorDevolucao] = useState('');

  const gerar = async () => {
    const v = Number(valor);
    if (!v) { toast({ title: 'Informe o valor', variant: 'destructive' }); return; }
    setGerando(true);
    try {
      const crId = contaReceberId ?? undefined;
      const r = comVencimento
        ? await provider.criarPixComVencimento(contaId, {
            valor: v, dataVencimento, devedorCpfCnpj: devedorDoc || undefined,
            devedorNome: devedorNome || undefined, contaReceberId: crId,
          })
        : await provider.criarPixImediato(contaId, {
            valor: v, devedorCpfCnpj: devedorDoc || undefined,
            devedorNome: devedorNome || undefined, contaReceberId: crId,
          });
      setResultado(r);
      toast({ title: 'Cobrança PIX criada', description: `txid ${r.txid}` });
    } catch (e) {
      toast({ title: 'Erro ao gerar PIX', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally { setGerando(false); }
  };

  const devolver = async () => {
    const v = Number(valorDevolucao);
    if (!e2eid || !v) { toast({ title: 'Informe e2eid e valor', variant: 'destructive' }); return; }
    try {
      const r = await provider.devolverPix(contaId, e2eid, v);
      toast({ title: 'Devolução solicitada', description: `Status ${r.status ?? '—'}` });
    } catch (e) { toast({ title: 'Erro na devolução', description: getApiErrorMessage(e), variant: 'destructive' }); }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader><CardTitle className="text-base">Gerar cobrança PIX</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <input id="comvenc" type="checkbox" checked={comVencimento} onChange={(e) => setComVencimento(e.target.checked)} />
            <Label htmlFor="comvenc">Com vencimento (cobv)</Label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Valor</Label><Input type="number" value={valor} onChange={(e) => setValor(e.target.value)} /></div>
            {comVencimento && (
              <div><Label>Vencimento</Label><Input type="date" value={dataVencimento} onChange={(e) => setDataVencimento(e.target.value)} /></div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Devedor CPF/CNPJ {comVencimento ? '' : '(opcional)'}</Label><Input value={devedorDoc} onChange={(e) => setDevedorDoc(e.target.value)} /></div>
            <div><Label>Devedor nome</Label><Input value={devedorNome} onChange={(e) => setDevedorNome(e.target.value)} /></div>
          </div>
          <div>
            <Label>Título a baixar quando pago (opcional)</Label>
            <TituloReceberSearchCombobox
              value={contaReceberId}
              onChange={setContaReceberId}
              onSelect={(t) => {
                if (!t) return;
                setValor(String(t.valorSaldo));
                if (comVencimento) setDataVencimento(t.dtVencimento.slice(0, 10));
                if (t.clienteNome && !devedorNome) setDevedorNome(t.clienteNome);
              }}
            />
          </div>
          <Button onClick={gerar} disabled={gerando}>
            {gerando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <QrCode className="mr-2 h-4 w-4" />}
            Gerar PIX
          </Button>
          {resultado && (
            <div className="space-y-2 rounded-lg border p-3 text-sm">
              <div className="flex justify-between"><span>txid</span><span className="break-all">{resultado.txid}</span></div>
              <div className="flex justify-between"><span>Situação</span><Badge>{resultado.situacao ?? '—'}</Badge></div>
              {resultado.pixCopiaECola && <CopiaCola label="Copia-e-cola" valor={resultado.pixCopiaECola} />}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Devolver um PIX recebido</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>endToEndId (e2eid)</Label><Input value={e2eid} onChange={(e) => setE2eid(e.target.value)} /></div>
          <div><Label>Valor a devolver</Label><Input type="number" value={valorDevolucao} onChange={(e) => setValorDevolucao(e.target.value)} /></div>
          <Button variant="destructive" onClick={devolver}>Solicitar devolução</Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Webhooks ─────────────────────────────────────────────────────────────

function WebhooksTab({ contaId, provider }: {
  contaId: number;
  provider: NonNullable<ReturnType<typeof useBancoIntegracaoProvider>['provider']>;
}) {
  const { toast } = useToast();
  const [urls, setUrls] = useState<Record<string, string>>({});

  const consultar = useCallback(async (tipo: WebhookTipo) => {
    try {
      const url = await provider.consultarWebhook(contaId, tipo);
      setUrls((s) => ({ ...s, [tipo]: url ?? '' }));
    } catch (e) { toast({ title: 'Erro ao consultar webhook', description: getApiErrorMessage(e), variant: 'destructive' }); }
  }, [provider, contaId, toast]);

  const registrar = async (tipo: WebhookTipo) => {
    const url = urls[tipo];
    if (!url) { toast({ title: 'Informe a URL (HTTPS)', variant: 'destructive' }); return; }
    try {
      await provider.registrarWebhook(contaId, tipo, url);
      toast({ title: 'Webhook registrado', description: tipo });
    } catch (e) { toast({ title: 'Erro ao registrar', description: getApiErrorMessage(e), variant: 'destructive' }); }
  };

  const excluir = async (tipo: WebhookTipo) => {
    try {
      await provider.excluirWebhook(contaId, tipo);
      setUrls((s) => ({ ...s, [tipo]: '' }));
      toast({ title: 'Webhook excluído', description: tipo });
    } catch (e) { toast({ title: 'Erro ao excluir', description: getApiErrorMessage(e), variant: 'destructive' }); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Webhooks por tipo</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {WEBHOOK_TIPOS.filter(({ value }) => !provider.webhookTipos || provider.webhookTipos.includes(value)).map(({ value, label }) => (
          <div key={value} className="flex flex-wrap items-end gap-2 border-b pb-3">
            <div className="min-w-[200px] flex-1">
              <Label>{label}</Label>
              <Input
                placeholder={provider.webhookPlaceholder ?? 'https://…/api/inter-webhook/…'}
                value={urls[value] ?? ''}
                onChange={(e) => setUrls((s) => ({ ...s, [value]: e.target.value }))}
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => consultar(value)}>Consultar</Button>
            <Button size="sm" onClick={() => registrar(value)}>Registrar</Button>
            <Button variant="destructive" size="sm" onClick={() => excluir(value)}>Excluir</Button>
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          {provider.webhookAjuda ?? 'A URL deve ser HTTPS e pública, apontando para o endpoint de recebimento do Udisys.'}
        </p>
      </CardContent>
    </Card>
  );
}

// ── Util: copia-e-cola ────────────────────────────────────────────────────────

function CopiaCola({ label, valor }: { label: string; valor: string }) {
  const { toast } = useToast();
  return (
    <div>
      <div className="mb-1 text-xs text-muted-foreground">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-muted px-2 py-1 text-xs">{valor}</code>
        <Button
          variant="ghost" size="icon"
          onClick={() => { navigator.clipboard.writeText(valor); toast({ title: 'Copiado' }); }}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
